import ErrorIcon from '@mui/icons-material/Error';
import { captureException, setContext, setExtra, setTags } from '@sentry/react';
import {
  PossibleRtkQueryErrors,
  VsApiError,
  VsGraphQLError,
  getErrorInstance,
  isUnAuthError,
} from '@vs/rtk/utils';
import type { SentryOpts } from '@vs/sentry-lib';
import { PortalButton } from '@vs/ui/portal/button';
import { PortalDialog } from '@vs/ui/portal/dialog';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import ErrorDialogContent from './error-info-templates/ErrorDialogContent';
import { isAppRenewChunkLossError, isChromeExtensionError } from './helper';
import { PortalSentryRef } from './type';
import useSetUserProfile from './useSetUserProfile';

const isKnownIssues = (error: unknown) =>
  [isAppRenewChunkLossError, isUnAuthError].some(assert => assert(error));

export type PortalSentryProps = {
  enableSentry?: boolean;
  sentryOptions?: SentryOpts;
  dialogMode?: 'detail' | 'compact';
  onMounted?: () => void;
};

export const PortalSentry = forwardRef<PortalSentryRef, PortalSentryProps>(
  (
    { enableSentry = false, sentryOptions, dialogMode = 'detail', onMounted },
    ref
  ) => {
    const { t } = useTranslation('shared');

    const [sentryId, setSentryId] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [errorObject, setErrorObject] = useState<unknown>();

    const refVar = useRef<
      Pick<PortalSentryProps, 'onMounted'> & { mounted: boolean }
    >({ onMounted, mounted: false });
    refVar.current.onMounted = onMounted;

    const logError = useCallback<PortalSentryRef['logError']>(
      (err, options) => {
        const showDialog = (id: string, error: unknown) => {
          if (options?.showErrorDialog) {
            setSentryId(id);
            setErrorObject(error);
            setOpenDialog(true);
          }
        };

        if (options?.extra) {
          setExtra('extra', options.extra);
        }
        const errInstance = getErrorInstance(err as PossibleRtkQueryErrors);
        let id: string;
        if (
          errInstance &&
          (errInstance instanceof VsApiError ||
            errInstance instanceof VsGraphQLError)
        ) {
          setExtra('status', errInstance.status);
          setExtra('fetch args', errInstance.fetchArgs);
          setExtra('cause', errInstance.cause);
          id = captureException(errInstance);
          showDialog(id, errInstance);
        } else {
          const exception = err as Error;
          setExtra('stack', exception?.stack);
          id = captureException(exception);
        }
        return id;
      },
      []
    );
    const setUserContext = useCallback<PortalSentryRef['setUserContext']>(
      contexts =>
        Object.entries(contexts).map(([k, v]) => {
          setContext(k, v);
        }),
      []
    );
    const setUserTags = useCallback<PortalSentryRef['setUserTags']>(
      tags => setTags(tags),
      []
    );

    useImperativeHandle(ref, () => ({
      logError,
      setUserContext,
      setUserTags,
    }));

    useSetUserProfile();

    useEffect(() => {
      (async () => {
        try {
          if (enableSentry) {
            const { initSentry } = await import('@vs/sentry-lib');
            initSentry({
              ...sentryOptions,
              beforeSend(event, hint) {
                // Even we have disabled Sentry global error log and handle error in 'unhandledrejection' and 'unhandledrejection' event
                // Sentry still collect global error. We prevent those known issues from logged by Sentry
                if (isKnownIssues(hint.originalException)) {
                  return null;
                }
                if (isChromeExtensionError(event)) {
                  return null;
                }
                return event;
              },
            });
          }
        } finally {
          if (!refVar.current.mounted) {
            refVar.current.mounted = true;
            refVar.current.onMounted?.();
          }
        }
      })();
    }, [enableSentry, sentryOptions]);

    useEffect(() => {
      const onUnhandledrejection = (e: PromiseRejectionEvent) => {
        const err = e.reason;
        if (!isKnownIssues(err)) {
          logError(err, { showErrorDialog: true });
        }
      };
      window.addEventListener('unhandledrejection', onUnhandledrejection);

      const onError = (e: ErrorEvent) => {
        const err = e.error;
        if (!isKnownIssues(err)) {
          logError(err, {
            showErrorDialog: true,
            extra: {
              filename: e.filename,
              lineno: e.lineno,
              colno: e.colno,
              message: e.message,
            },
          });
        }
      };
      window.addEventListener('error', onError);

      return () => {
        window.removeEventListener('error', onError);
        window.removeEventListener('unhandledrejection', onUnhandledrejection);
      };
    }, [logError]);

    function handleDialogClose() {
      setOpenDialog(false);
    }

    const isDetailMode = dialogMode === 'detail';
    return (
      <PortalDialog
        size={isDetailMode ? 'lg' : 'md'}
        open={openDialog}
        titleIcon={
          <ErrorIcon className="text-[32px] me-2 text-vs-error-focus" />
        }
        title={t('dialog.unexpected_err.title', {
          defaultValue: 'Error',
        })}
        content={
          <ErrorDialogContent
            error={errorObject}
            sentryId={sentryId}
            showFullErrMsg={isDetailMode}
          />
        }
        actions={
          <div className="text-right">
            <PortalButton
              label={t('common.btn.close', {
                defaultValue: 'Close',
              })}
              onClick={handleDialogClose}
              type="secondary"
              variant="contained"
            />
          </div>
        }
      />
    );
  }
);

export default PortalSentry;
